import { accessToken } from './supabase';
import type { ItemDetail, PageFull, PageSummary, SearchResult, Task, WorkItem } from './types';

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await accessToken();
  const res = await fetch(`/api/${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  let body: any = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(text.slice(0, 300) || `HTTP ${res.status}`, res.status);
  }
  if (!res.ok) throw new ApiError(body?.error || `HTTP ${res.status}`, res.status);
  return body as T;
}

const post = <T>(path: string, body: unknown) => call<T>(path, { method: 'POST', body: JSON.stringify(body) });

export interface Person {
  name: string;
  email: string;
  aliases?: string[];
}

export const getMe = () =>
  call<{ email: string; role: 'developer' | 'reporter'; person: Person | null }>('me');

export const getPeople = () => call<{ people: Person[] }>('people');

export interface Cycle {
  id: string;
  key: string;
  title: string;
  status: string;
  starts_on: string;
  target_date: string;
}

// A row of tracker.v_my_work: the item plus where it sits in its owner's list.
export type MyWorkItem = WorkItem & {
  section: 'urgent' | 'cycle' | 'untriaged' | 'backlog' | 'waiting' | 'hold';
  pick_order: number;
  in_cycle: boolean;
};

export const getMyWork = (who?: string) =>
  call<{ owner: string; items: MyWorkItem[]; openTasks: Record<string, number>; warnings: string[]; cycle: Cycle | null }>(
    `my-work${who ? `?who=${encodeURIComponent(who)}` : ''}`,
  );

export const rankItem = (key: string, position: number | null) =>
  post<{ item: WorkItem; position: number | null }>(`item/${encodeURIComponent(key)}/rank`, { position });

export const setInCycle = (key: string, inCycle: boolean) =>
  post<{ item: WorkItem }>(`item/${encodeURIComponent(key)}/cycle`, { in: inCycle });

export const startCycle = (starts_on: string, weeks = 2) =>
  post<{ key: string; cycle: Cycle | null }>('cycles', { starts_on, weeks });

// setPriority: p0..p3, or null to send the item back to untriaged (developer role).
export const setPriority = (key: string, priority: string | null) =>
  post<{ item: WorkItem }>(`item/${encodeURIComponent(key)}/edit`, { priority: priority ?? 'none' });

export const setDue = (key: string, due_date: string | null) =>
  post<{ item: WorkItem }>(`item/${encodeURIComponent(key)}/due`, { due_date: due_date ?? '' });

export interface MilestoneRef {
  id: string;
  key: string;
  title: string;
  status: string;
  target_date: string | null;
}

export interface MilestoneProgress extends MilestoneRef {
  components: string[];
  items: number;
  completed: number;
  cancelled: number;
  in_flight: number;
  remaining: number;
  last_activity: string | null;
}

export interface Developer {
  id: string;
  email: string;
  display_name: string;
}

export const getBoard = (all = false) =>
  call<{ items: WorkItem[]; openTasks: Record<string, number>; milestones: MilestoneRef[] }>(
    `board${all ? '?all=1' : ''}`,
  );

export const getMilestones = () => call<{ milestones: MilestoneProgress[] }>('milestones');

export const getDevelopers = () => call<{ developers: Developer[] }>('developers');

export const saveMilestone = (m: {
  key: string;
  title?: string;
  summary?: string;
  target_date?: string | null;
  status?: string;
  components?: string[];
}) => post<{ milestone: MilestoneProgress }>('milestones', m);

export const assign = (key: string, owner: string | null) =>
  post<{ item: WorkItem }>(`item/${encodeURIComponent(key)}/assign`, { owner });

export const setMilestone = (key: string, milestone: string | null) =>
  post<{ item: WorkItem }>(`item/${encodeURIComponent(key)}/milestone`, { milestone });

export const getMine = () => call<{ items: WorkItem[] }>('mine');

export const getItem = (key: string) => call<ItemDetail>(`item/${encodeURIComponent(key)}`);

export const getPages = () => call<{ pages: PageSummary[] }>('pages');

export const getPage = (path: string) => call<{ page: PageFull }>(`page?path=${encodeURIComponent(path)}`);

export const search = (q: string) =>
  call<{ results: SearchResult[]; semantic: boolean }>(`search?q=${encodeURIComponent(q)}`);

export interface NewTicket {
  title: string;
  detail: string;
  kind: 'bug' | 'feature';
  urgency: 'blocking' | 'soon' | 'idea';
  where?: string;
}

export const fileTicket = (t: NewTicket) => post<{ item: WorkItem }>('tickets', t);

export const setStatus = (key: string, status: string, resolution?: string | null) =>
  post<{ item: WorkItem }>(`item/${encodeURIComponent(key)}/status`, { status, resolution });

export const taskDone = (key: string, seq: number) =>
  post<{ task: Task }>(`item/${encodeURIComponent(key)}/task`, { action: 'done', seq });

export const taskDrop = (key: string, seq: number, note?: string) =>
  post<{ task: Task }>(`item/${encodeURIComponent(key)}/task`, { action: 'drop', seq, note });

export const taskStart = (key: string, seq: number) =>
  post<{ task: Task }>(`item/${encodeURIComponent(key)}/task`, { action: 'start', seq });

export const taskNote = (key: string, seq: number, text: string) =>
  post<{ task: Task }>(`item/${encodeURIComponent(key)}/task`, { action: 'note', seq, text });

export const taskAdd = (key: string, title: string) =>
  post<{ task: Task }>(`item/${encodeURIComponent(key)}/task`, { action: 'add', title });

export const addNote = (key: string, text: string) =>
  post<{ ok: true }>(`item/${encodeURIComponent(key)}/note`, { text });

// The page list doubles as the wikilink resolver, so it is fetched once and shared.
let pagesPromise: Promise<PageSummary[]> | null = null;
export function allPages(): Promise<PageSummary[]> {
  if (!pagesPromise) pagesPromise = getPages().then((r) => r.pages);
  return pagesPromise;
}
